import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { Product } from '@spartacus/core';
import { SlbOccConnector } from 'app/core/connectors/slb-occ.connector';
import { SlbOccServicesEndPoints } from 'app/core/config/slb-occ.endpoints';
import { SlbProductService } from 'app/core/services/slb-product.service';
import { CatalogProductResponse } from '../models/product-catalog.model';
import { ProductDetailResponse } from '../models/product-detail.model';
import { TagMapping } from '../models/tag-mapping.model';
import { MarketplaceServiceClient } from './marketplace-service-client';
import { extractSearchText } from '../utils/extract-search-text';
import { deduplicateNonSapProducts } from '../utils/deduplicate-non-sap-products';

@Injectable({ providedIn: 'root' })
export class CatalogService {
  private http = inject(HttpClient);
  private occConnector = inject(SlbOccConnector);
  private productService = inject(SlbProductService);
  private readonly marketplaceServiceClient = inject(MarketplaceServiceClient);

  // !!! consider using Product and other Spartacus types as core models in marketplace app
  // !!! instead of redefining them, and do additional mapping
  getCatalog(): Observable<CatalogProductResponse[]> {
    return forkJoin([
      // marketplaceVisible:true — Solr filter for the marketplace channel.
      // Without it the backend defaults to commerceVisible:true, which targets the Commerce storefront.
      this.occConnector.get<{ products: Product[] }>(
        SlbOccServicesEndPoints.plpProducts,
        { categoryCode: 'plugin', query: ':relevance:marketplaceVisible:true', size: 1000, page: 0 }
      ).pipe(catchError(() => of({ products: [] as Product[] }))),
      this.occConnector.get<{ products: Product[] }>(
        SlbOccServicesEndPoints.plpISVProducts,
        { query: '', size: 1000, page: 0, lang: 'en', currency: 'USD' }
      ).pipe(catchError(() => of({ products: [] as Product[] }))),
      this.http.get<{ products: CatalogProductResponse[] }>('assets/data/non-sap-products.json'),
      this.http.get<Record<string, ProductDetailResponse>>('assets/data/non-sap-product-details.json'),
    ]).pipe(
      map(([catalog, search, nonSap, nonSapDetails]) => {
        const searchMap = new Map(search.products.map(p => [p.code, p]));

        const sapProducts: CatalogProductResponse[] = catalog.products.map(p => {
          const searchProduct = searchMap.get(p.code!);
          const merged = searchProduct ? { ...searchProduct, ...p } : p;
          return this.mapProductToCatalogResponse(merged);
        });

        const mappedNonSap = nonSap.products.map(p => ({
          ...p,
          _isNonSap: true as const,
          _searchText: extractSearchText(nonSapDetails[p.code] ?? p),
        }));
        return [...sapProducts, ...deduplicateNonSapProducts(sapProducts, mappedNonSap)];
      })
    );
  }

  getProductDetails(): Observable<Record<string, ProductDetailResponse>> {
    return this.http.get<Record<string, ProductDetailResponse>>('assets/data/non-sap-product-details.json');
  }

  getProductDetail(code: string): Observable<ProductDetailResponse> {
    return this.productService.getProductByCode(code).pipe(
      map(product => this.mapProductToDetailResponse(product!))
    );
  }

  getTagMapping(): Observable<TagMapping> {
    return this.marketplaceServiceClient.getBadges();
  }

  private mapProductToCatalogResponse(p: Product): CatalogProductResponse {
    return {
      code: p.code ?? '',
      name: p.name ?? '',
      vendorName: p.vendorName ?? '',
      briefDescription: p.briefDescription ?? '',
      platformType: p.platformType ?? '',
      domains: p.domains,
      hosting: p.hosting,
      _searchText: extractSearchText(p),
    };
  }

  private mapProductToDetailResponse(p: Product): ProductDetailResponse {
    const detail = p as Product & { trialType?: 'sandbox' | 'free' };
    return {
      briefDescription: p.briefDescription ?? '',
      categories: p.categories?.map(c => ({ ...c, code: c.code ?? '' })),
      challenges: p.challenges ?? [],
      countryOFOrigin: p.countryOFOrigin ?? '',
      overview: p.overview ?? '',
      purchasable: p.purchasable,
      remarks: p.remarks ?? '',
      specifications: p.specifications ?? '',
      domains: p.domains ?? [],
      ean: p.ean ?? '',
      galleryImages: (p.galleryImages ?? []).map(img => ({ url: img.url ?? '' })),
      hosting: p.hosting ?? '',
      marketingMaterials: (p.marketingMaterials ?? []).map(m => ({
        attachmentType: m.attachmentType ?? '',
        description: m.description ?? '',
        media: { url: m.media?.url ?? '' },
        name: m.name ?? '',
      })),
      platformType: p.platformType ?? '',
      trialType: detail.trialType,
      variantOptions: p.variantOptions?.map(v => ({
        allowAddQuantity: v.allowAddQuantity ?? false,
        code: v.code ?? '',
        url: v.url ?? '',
        variantOptionQualifiers: (v.variantOptionQualifiers ?? []).map(q => ({
          name: q.name ?? '',
          qualifier: q.qualifier ?? '',
          value: q.value ?? '',
        })),
      })),
      vendorName: p.vendorName ?? '',
      vendorUrlLink: p.vendorUrlLink ?? '',
      lifeCycleConventional: p.lifeCycleConventional,
    };
  }
}
